import * as React from "react";
import {useState} from "react";
import {Controller, useFormContext} from "react-hook-form";
import {Box, Checkbox, Divider, FormControl, InputLabel, ListItemText, MenuItem, Select, TextField} from "@mui/material";
import {CheckboxProps} from "@mui/material/Checkbox/Checkbox";
import {merge} from "lodash";

type InputOption = {
   id: string
   label: string
   subtext?: string
}

type FormInputSelectMultipleProps = Omit<CheckboxProps, 'name' | 'label'> & {
   name: string
   label: string
   options: InputOption[]
   onChange?: () => void
   icon?: React.ReactNode
   /** When true, renders a search input at the top of the dropdown to filter options */
   allowSearch?: boolean
}

let nextId: number = 100;

export const FormInputSelectMultiple: React.FC<FormInputSelectMultipleProps> = ({
                                                                                   name,
                                                                                   label,
                                                                                   options,
                                                                                   onChange: onParentChange,
                                                                                   icon,
                                                                                   allowSearch,
                                                                                   ...rest
                                                                                }) => {
   const {style} = rest;

   const methods = useFormContext();
   const {control} = methods;

   const [searchText, setSearchText] = useState('');

   const labelId = 'multiselect_' + (nextId++);
   const inputId = 'multiselectinput_' + (nextId++);

   const newStyle = merge(style, {
      marginBottom: '8px',
   });

   const selectStyle = {
      backgroundColor: 'transparent',
      border: '1px solid #d0d0d0'
   };

   // Filter options based on search text (matches label or subtext)
   const filteredOptions = allowSearch && searchText
      ? options.filter(option => {
         const searchLower = searchText.toLowerCase();
         const labelMatch = option.label.toLowerCase().includes(searchLower);
         const subtextMatch = option.subtext?.toLowerCase().includes(searchLower);
         return labelMatch || subtextMatch;
      })
      : options;

   return (
      <FormControl component={"fieldset"} fullWidth variant={"filled"} margin={"none"} style={newStyle} size={"small"}>
         <InputLabel id={labelId}>
            <div style={{display: 'flex', alignItems: 'middle', gap: '10px'}}>
               {icon && icon} {label}
            </div>
         </InputLabel>
         <Controller
            control={control}
            name={name}
            render={({field: {onChange, value}, fieldState: {error}}) => {
               return <Select
                  disableUnderline
                  labelId={labelId}
                  id={inputId}
                  multiple
                  style={selectStyle}
                  value={value || []}
                  onClose={() => setSearchText('')}
                  onChange={(e) => {
                     if (e.target.value?.includes('_reset')) {
                        onChange({
                           ...e,
                           target: {
                              value: []
                           }
                        });
                     } else {
                        console.log(e);
                        onChange(e);
                     }
                     onParentChange && onParentChange();
                  }}
                  renderValue={(selected) => {
                     if (!selected || selected.length === 0) {
                        return <>None</>;
                     }
                     const firstOption = options.find(option => option.id === selected[0]);
                     const firstLabel = firstOption?.label || selected[0];
                     if (selected.length === 1) {
                        return <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{firstLabel}</span>;
                     }
                     const remaining = selected.length - 1;
                     return (
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                           {firstLabel} +{remaining}
                        </span>
                     );
                  }}>
                  {allowSearch && (
                     <Box sx={{ px: 1, py: 1, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                        <TextField
                           size="small"
                           autoFocus
                           placeholder="Search..."
                           fullWidth
                           value={searchText}
                           onChange={(e) => setSearchText(e.target.value)}
                           onKeyDown={(e) => e.stopPropagation()}
                           onClick={(e) => e.stopPropagation()}
                        />
                     </Box>
                  )}
                  {filteredOptions.map(option => {
                     return <MenuItem key={'opt_' + option.id} value={option.id}>
                        <Checkbox checked={(value || []).indexOf(option.id) > -1}/>
                        <ListItemText primary={option.label} secondary={option.subtext}/>
                     </MenuItem>
                  })}
                  <Divider/>
                  <MenuItem value={'_reset'} disabled={(value||[]).length===0}>
                     <ListItemText>
                        Clear
                     </ListItemText>
                  </MenuItem>
               </Select>
            }}
         />
      </FormControl>
   );
};
